import { useEffect, useMemo, useRef, useState } from 'react';
import { Checkbox } from '../../components/Checkbox';
import { Counter } from '../../components/Counter';
import { DayStripSelector } from '../../components/DayStripSelector';
import { Modal } from '../../components/Modal';
import { ModalTotalFooter } from '../../components/ModalTotalFooter';
import { OptionRow } from '../../components/OptionRow';
import { TimeslotChipSelector } from '../../components/TimeslotChipSelector';
import { formatCurrency } from '../../utils/formatCurrency';
import {
  ApplyAllLabel,
  ApplyAllRow,
  Content,
  OptionList,
  PlainSection,
  QuantityInfo,
  QuantityLabel,
  QuantityPrice,
  QuantityRow,
  Section,
  SectionHeading,
  TimesBlock,
} from './ExtrasEditModal.styles';
import type {
  ExtrasEditModalProps,
  ExtrasEditModalType,
  ExtrasEditOption,
  ExtrasEditSelection,
} from './ExtrasEditModal.types';

const DEFAULT_TITLES: Record<ExtrasEditModalType, string> = {
  per_date: 'Select date',
  per_date_range: 'Select date range',
  per_time_range: 'Select time range',
  per_date_time: 'Select date and time',
  per_date_time_range: 'Select date and time range',
  quantity: 'Select quantity',
};

const SHARED_KEY = '';

const buildSelections = (initialSelections: ExtrasEditSelection[]) => {
  const initial: Record<string, string | undefined> = {};
  initialSelections.forEach((selection) => {
    initial[selection.guestId ?? SHARED_KEY] = selection.optionId;
  });
  if (initial[SHARED_KEY] === undefined) {
    const firstSelected = initialSelections[0]?.optionId;
    if (firstSelected) initial[SHARED_KEY] = firstSelected;
  }
  return initial;
};

const dateOf = (options: ExtrasEditOption[], optionId: string | undefined) =>
  options.find((option) => option.id === optionId)?.date;

export function ExtrasEditModal({
  open,
  onClose,
  type,
  extraTitle,
  options = [],
  guests,
  perPerson = false,
  initialSelections = [],
  initialApplyToAll = false,
  unitPrice,
  maxQuantity,
  initialQuantity = 1,
  rangeLabel,
  currencyCode = 'GBP',
  locale = 'en-GB',
  onConfirm,
  labels = {},
}: ExtrasEditModalProps) {
  const {
    title = DEFAULT_TITLES[type],
    close = 'Close',
    total = 'Total',
    confirm = 'Confirm',
    applyToAll: applyToAllLabel = 'Apply same selection to all guests',
    quantity: quantityLabel = 'Quantity',
    perUnit = '/ unit',
    perGuest = '/ guest',
    weekdays,
    previousWeek,
    nextWeek,
    timesLabel = 'Time',
  } = labels;

  const guestList = useMemo(() => guests ?? [], [guests]);
  const guestsMode = guestList.length > 1;
  const usesDayStrip = type === 'per_date_time' || type === 'per_date_time_range';

  const [quantity, setQuantity] = useState(initialQuantity);
  const [applyToAll, setApplyToAll] = useState(guestsMode ? initialApplyToAll : false);
  const [selections, setSelections] = useState<Record<string, string | undefined>>(() =>
    buildSelections(initialSelections)
  );
  const [activeDates, setActiveDates] = useState<Record<string, string | undefined>>({});

  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setQuantity(initialQuantity);
      setApplyToAll(guestsMode ? initialApplyToAll : false);
      setSelections(buildSelections(initialSelections));
      setActiveDates({});
    }
    wasOpen.current = open;
  }, [open, initialQuantity, initialApplyToAll, guestsMode, initialSelections]);

  const stripDates = useMemo(
    () => options.map((option) => option.date).filter((date): date is string => !!date),
    [options]
  );

  const sections = useMemo(
    () =>
      guestsMode && !applyToAll
        ? guestList.map((guest) => ({ key: guest.id, heading: guest.label }))
        : [{ key: SHARED_KEY, heading: undefined }],
    [guestsMode, applyToAll, guestList]
  );

  const activeDateFor = (key: string) =>
    activeDates[key] ?? dateOf(options, selections[key]) ?? stripDates[0];

  const handleApplyToAllToggle = (checked: boolean) => {
    setApplyToAll(checked);
    if (checked) {
      setSelections((prev) => {
        const shared = prev[SHARED_KEY] ?? guestList.map((g) => prev[g.id]).find(Boolean);
        return { ...prev, [SHARED_KEY]: shared };
      });
    } else {
      setSelections((prev) => {
        const next = { ...prev };
        guestList.forEach((guest) => {
          if (next[guest.id] === undefined) next[guest.id] = prev[SHARED_KEY];
        });
        return next;
      });
    }
  };

  const handleOptionSelect = (key: string, optionId: string) => {
    setSelections((prev) => ({ ...prev, [key]: optionId }));
  };

  const handleDateSelect = (key: string, date: string) => {
    setActiveDates((prev) => ({ ...prev, [key]: date }));
    setSelections((prev) => {
      if (prev[key] && dateOf(options, prev[key]) !== date) {
        return { ...prev, [key]: undefined };
      }
      return prev;
    });
  };

  const priceOf = (optionId: string | undefined) => {
    if (!optionId) return 0;
    return options.find((option) => option.id === optionId)?.price?.amount ?? 0;
  };

  const totalAmount = useMemo(() => {
    if (type === 'quantity') {
      return (unitPrice?.amount ?? 0) * quantity;
    }
    if (guestsMode && applyToAll) {
      return priceOf(selections[SHARED_KEY]) * guestList.length;
    }
    return sections.reduce((sum, section) => sum + priceOf(selections[section.key]), 0);
  }, [type, unitPrice, quantity, guestsMode, applyToAll, guestList, sections, selections, options]);

  const allSectionsSelected = sections.every((section) => !!selections[section.key]);
  const confirmDisabled = type !== 'quantity' && !allSectionsSelected;

  const handleConfirm = () => {
    if (type === 'quantity') {
      onConfirm({ type: 'quantity', quantity });
      return;
    }
    if (guestsMode && !applyToAll) {
      const guestSelections = guestList.flatMap((guest) => {
        const optionId = selections[guest.id];
        return optionId ? [{ guestId: guest.id, optionId }] : [];
      });
      onConfirm({ type: 'options', applyToAll: false, selections: guestSelections });
      return;
    }
    const sharedOptionId = selections[SHARED_KEY];
    if (!sharedOptionId) return;
    onConfirm({
      type: 'options',
      applyToAll: guestsMode,
      selections: [{ optionId: sharedOptionId }],
    });
  };

  const optionSublabel = (option: ExtrasEditOption) => {
    if (!option.price) return undefined;
    const formatted = formatCurrency(option.price.amount, option.price.currencyCode, { locale });
    return perPerson ? `${formatted} ${perGuest}` : formatted;
  };

  const renderSectionContent = (key: string, heading: string | undefined) => {
    if (type === 'quantity') {
      return (
        <QuantityRow>
          <QuantityInfo>
            <QuantityLabel>{quantityLabel}</QuantityLabel>
            {unitPrice && (
              <QuantityPrice>
                {formatCurrency(unitPrice.amount, unitPrice.currencyCode, { locale })} {perUnit}
              </QuantityPrice>
            )}
          </QuantityInfo>
          <Counter value={quantity} min={1} max={maxQuantity ?? undefined} onChange={setQuantity} />
        </QuantityRow>
      );
    }

    const activeDate = usesDayStrip ? activeDateFor(key) : undefined;
    const visibleOptions = usesDayStrip
      ? options.filter((option) => option.date === activeDate)
      : options;

    const optionsControl =
      type === 'per_date_time' ? (
        <TimeslotChipSelector
          wrap
          timeslots={visibleOptions.map((option) => ({
            value: option.id,
            label: option.label,
            price: option.price?.amount,
            currencyCode: option.price?.currencyCode,
          }))}
          selectedTimeslot={selections[key]}
          onTimeslotSelect={(optionId) => handleOptionSelect(key, optionId)}
          labels={{ title: timesLabel, locale }}
        />
      ) : (
        <OptionList role="radiogroup" aria-label={heading ?? title}>
          {visibleOptions.map((option) => (
            <OptionRow
              key={option.id}
              mode="radio"
              label={option.label}
              sublabel={optionSublabel(option)}
              selected={selections[key] === option.id}
              onClick={() => handleOptionSelect(key, option.id)}
            />
          ))}
        </OptionList>
      );

    if (!usesDayStrip) return optionsControl;

    return (
      <>
        <DayStripSelector
          dates={stripDates}
          selectedDate={activeDate}
          onDateSelect={(date) => handleDateSelect(key, date)}
          rangeLabel={rangeLabel}
          labels={{ weekdays, previousWeek, nextWeek }}
        />
        <TimesBlock>{optionsControl}</TimesBlock>
      </>
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="lg"
      labels={{ close }}
      footer={
        <ModalTotalFooter
          title={extraTitle}
          totalLabel={total}
          totalAmount={totalAmount}
          currencyCode={currencyCode}
          locale={locale}
          confirmLabel={confirm}
          onConfirm={handleConfirm}
          confirmDisabled={confirmDisabled}
        />
      }
    >
      <Content>
        {guestsMode && type !== 'quantity' && (
          <ApplyAllRow>
            <ApplyAllLabel>{applyToAllLabel}</ApplyAllLabel>
            <Checkbox
              checked={applyToAll}
              onChange={handleApplyToAllToggle}
              aria-label={applyToAllLabel}
            />
          </ApplyAllRow>
        )}
        {sections.map((section) =>
          section.heading ? (
            <Section key={section.key}>
              <SectionHeading>{section.heading}</SectionHeading>
              {renderSectionContent(section.key, section.heading)}
            </Section>
          ) : (
            <PlainSection key={section.key}>
              {renderSectionContent(section.key, section.heading)}
            </PlainSection>
          )
        )}
      </Content>
    </Modal>
  );
}
